/**
 * Fund API Module
 *
 * This module provides functions for interacting with the fund-related endpoints
 * of the WealthBee Enterprise API.
 */
import axios from "axios";
import { Fund } from "../types/Fund";
import getServiceEndpoint from "./ServiceEndpoint";
import { buildClientHeader } from "./buildHeader";

/**
 * Retrieves all funds from the API.
 *
 * @returns A promise that resolves to an array of Fund objects.
 * If the request fails, returns an empty array.
 */
export const getFunds = async (): Promise<Fund[]> => {
  try {
    const response = await axios.get(
      `${getServiceEndpoint()}/funds`,
      buildClientHeader()
    );
    if (response.status !== 200 || response.data === undefined) {
      console.log("Failed to retrieve import templates", response.statusText);
      return [];
    }
    return response.data ?? [];
  } catch (error) {
    console.error("Error getting funds:", error);
  }

  return [];
};

/**
 * Retrieves a specific fund by its ID.
 *
 * @param id - The unique identifier of the fund to retrieve.
 * @returns A promise that resolves to a Fund object.
 * @throws Will throw an error if the API request fails.
 */
export const getFundById = async (id: number): Promise<Fund> => {
  const response = await axios.get(
    `${getServiceEndpoint()}/funds/${id}`,
    buildClientHeader()
  );
  return response.data;
};

/**
 * Creates a new fund or updates an existing one.
 *
 * @param fund - The fund data to create or update.
 * @returns A promise that resolves to a boolean indicating success (true) or failure (false).
 */
export const addOrUpdateFund = async (
  fund: Partial<Fund>
): Promise<boolean> => {
  try {
    const response = await axios.post(
      `${getServiceEndpoint()}/funds`,
      JSON.stringify(fund),
      buildClientHeader()
    );

    if (response.status !== 200) {
      console.error("Failed to save fund", response.statusText);
      return false;
    }
  } catch (error) {
    console.error("Error saving fund:", error);
    return false;
  }
  return true;
};

/**
 * Retrieves NAV data for funds based on reporting period.
 *
 * @param reportingPeriodId - The reporting period identifier to filter NAV data.
 * @returns A promise that resolves to the NAV data.
 * If the request fails, returns null.
 */
export const getFundsNav = async (reportingPeriodId: number): Promise<any> => {
  try {
    const response = await axios.get(
      `${getServiceEndpoint()}/funds/nav?reportingPeriodId=${reportingPeriodId}`,
      buildClientHeader()
    );
    if (response.status !== 200 || response.data === undefined) {
      console.log("Failed to retrieve funds NAV data", response.statusText);
      return null;
    }
    return response.data;
  } catch (error) {
    console.error("Error getting funds NAV:", error);
    return null;
  }
};

/**
 * Deletes a fund by its ID.
 *
 * @param id - The unique identifier of the fund to delete.
 * @returns A promise that resolves to a boolean indicating success (always returns true).
 * @todo Implement proper error handling and return actual success/failure status.
 */
export const deleteFundById = async (id: number): Promise<boolean> => {
  const response = await axios.delete(
    `${getServiceEndpoint()}/funds/${id}`,
    buildClientHeader()
  );
  if (response.status !== 200) {
    console.error("Failed to delete fund", response.statusText);
    return false;
  }

  return true;
};
